import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';

import { RoutePath } from '@components/AppRouter/constants';
import { NEW_ENTITY_ID } from '@components/AppRouter/hooks/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { useToggleFullSize } from '@src/utils/hooks/useToggleFullSize';
import { objectFieldsToCamelCase } from '@src/utils/objectFieldsToCamelCase';
import { confirmDialogStore } from '@stores/confirmDialog';
import { constantStore } from '@stores/constantStore';
import { controlsStore } from '@stores/dictionaryStores/controlsStore/controlsStore';
import type { ControlDtoDefId } from '@stores/dictionaryStores/controlsStore/controlsStore';

import { columns } from '../columns';

const { TABLE_NAMES } = constantStore;

export const ActionButton = observer(({ update }: { update: () => void }) => {
  const { push } = useHistory();
  const { groupDelete, fullSize, checked, setChecked, toggleFullSize } = controlsStore;
  const { open: openConfirmDialog } = confirmDialogStore;

  const onToggleFullSize = useToggleFullSize({
    tableName: TABLE_NAMES.DICTIONARY_CONTROLS,
    columns,
    fullSize,
    toggle: toggleFullSize,
    update,
  });

  const permission = usePermission('CmControl');

  const onCreate = useCallback(() => {
    push(`${RoutePath.DICTIONARY_CONTROLS}/${NEW_ENTITY_ID}`);
  }, [push]);

  const onEdit = useCallback(() => {
    push(`${RoutePath.DICTIONARY_CONTROLS}/${checked[0]?.ctrl_code}`);
  }, [checked, push]);

  const onDelete = useCallback(() => {
    openConfirmDialog({
      text: `Удалить выбранные элементы (${checked.length})?`,
      onOk: () => {
        const items = checked.map((item) => objectFieldsToCamelCase(item));

        groupDelete(items as ControlDtoDefId[]).then(() => {
          setChecked([]);
          update();
        });
      },
    });
  }, [checked, groupDelete, openConfirmDialog, setChecked, update]);

  return (
    <>
      {permission.CREATE && <IconButton onClick={onCreate} iconName="add" title="Добавить" />}
      {permission.UPDATE && (
        <IconButton onClick={onEdit} disabled={checked.length !== 1} iconName="edit" title="Редактировать" />
      )}
      {permission.DELETE && (
        <IconButton onClick={onDelete} disabled={checked.length === 0} iconName="delete" title="Удалить" />
      )}
      <IconButton
        onClick={onToggleFullSize}
        iconName={fullSize ? 'fullScreenOpen' : 'fullScreenClose'}
        title={fullSize ? 'Уменьшить' : 'Увеличить'}
      />
    </>
  );
});
